/**
 * Where a signed-in person lands — `ob-4` (docs/ONBOARDING.md O7).
 *
 * `/` is a fork, not a page: depending on what the account actually holds, the
 * right first screen is a set of books, a work queue, or the group view across
 * several businesses. That fork used to be four inline `if`s in `router.tsx`
 * with no test, which is how the O7 case ("an owner of several businesses should
 * get one dashboard across all of them") stayed unbuilt while every piece of it
 * already existed.
 *
 * It is a pure function of the memberships, so `postLoginLanding.test.ts` can
 * pin every branch — including the ones that are painful to reproduce by hand,
 * like a practice employee with no books of their own.
 *
 * Pending-invite resumes stay in the route component: they read localStorage, so
 * they are not a function of the memberships and do not belong here.
 *
 * @since ob-4 (2026-07-29)
 */

import { ownsPractice } from './officeIdentity';
import { isOwnBusiness, type BusinessMembership } from './myBusinesses';

/** Just enough of an `Org` to decide. Matches `types/db.ts`'s `Org`. */
export type LandingMembership = BusinessMembership;

export const LANDING_ROUTES = {
    /** Somebody's employee and nobody's owner — they have no books to land on. */
    workplace: '/my-workplace',
    /** A practice employee: every membership is scoped `staff`. */
    work: '/my-work',
    /** O7 - two or more businesses of their own. */
    group: '/my-businesses',
    /** The ordinary case: one set of books. */
    dashboard: '/dashboard',
} as const;

export type LandingRoute = (typeof LANDING_ROUTES)[keyof typeof LANDING_ROUTES];

/**
 * How many of these are the person's OWN businesses: owned, and neither a
 * client their office created (so owns) nor a retired twin (mybiz).
 */
export function ownedCount(orgs: readonly LandingMembership[]): number {
    return orgs.filter(isOwnBusiness).length;
}

/**
 * Do they run a practice? (ob-5, O8.)
 *
 * Ownership, not membership: a practice employee is already answered one branch
 * earlier by the all-`staff` rule, and someone merely invited into a firm's org
 * has a role there rather than a roster of their own.
 *
 * Lives in `officeIdentity.ts` now and is re-exported here: the landing fork and
 * the header's Office pill were answering "do they have an office?" with two
 * different rules, and the pill's was the loose one.
 */
export { ownsPractice };

/**
 * The first screen after sign-in.
 *
 * Order matters and is not arbitrary:
 *
 *  1. **No membership at all** → My Workplace, which forwards to `/onboarding`
 *     for someone who is neither an employee nor an owner.
 *  2. **Every membership is `staff`** → their work queue. A practice employee
 *     has no books of their own, so a dashboard would be someone else's.
 *  3. **They own a practice** -> the dashboard. See the note below.
 *  4. **Two or more owned** -> the group view (O7). An owner of several
 *     businesses has no single "my dashboard"; picking one of them arbitrarily
 *     is the thing this replaces.
 *  5. Otherwise the books.
 *
 * NOTE: 3 sits ABOVE 4 on purpose, and that ordering still matters even though both
 * now answer the same route. A CA who keeps their own books alongside the
 * practice owns two orgs, so without this branch they would trip the "2+ owned"
 * rule and land on the group view instead.
 *
 * NOTE: 3 USED TO GO TO `/practice-crm` (O8, ob-5): a consultant's work is their
 * client roster, so the roster was made their home. Founder, 8 Sep 2026: *"every
 * time someone open karr it lands on this /practice-crm, it should rather land
 * on /dashboard"*. The roster is still one click away in the sidebar and is
 * still where the office work happens; it is simply no longer what opens. If
 * this is ever reconsidered, the branch is here and the reasoning above is the
 * argument that was made for it.
 */
export function postLoginRoute(orgs: readonly LandingMembership[]): LandingRoute {
    if (orgs.length === 0) return LANDING_ROUTES.workplace;
    if (orgs.every(o => o.role === 'staff')) return LANDING_ROUTES.work;
    if (ownsPractice(orgs)) return LANDING_ROUTES.dashboard;
    if (ownedCount(orgs) > 1) return LANDING_ROUTES.group;
    return LANDING_ROUTES.dashboard;
}
